import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import ScheduleFormModal from './components/ScheduleFormModal';
import ScheduleTable from './components/ScheduleTable';
import SchedulePreview from './components/SchedulePreview';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import PeakHoursConfig from './components/PeakHoursConfig';
import { routeService } from '../../services/routeService';
import { scheduleService } from '../../services/scheduleService';

const SchedulesManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [showPeakConfig, setShowPeakConfig] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [peakHours, setPeakHours] = useState({
    morning: { start: '07:30', end: '09:30' },
    evening: { start: '17:00', end: '19:00' },
    multiplier: 1.2
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [routesData, schedulesData] = await Promise.all([
        routeService?.getAll(),
        scheduleService?.getAll()
      ]);
      setRoutes(routesData?.map(r => ({
        id: r?.id,
        route_name: r?.routeName,
        start_point: r?.startPoint,
        end_point: r?.endPoint,
        active: r?.isActive
      })));
      setSchedules(schedulesData?.map(s => ({
        id: s?.id,
        route_id: s?.routeId,
        start_time: s?.startTime,
        end_time: s?.endTime,
        duration_minutes: s?.durationMinutes,
        frequency_minutes: s?.frequencyMinutes,
        days_mask: s?.daysMask
      })));
    } catch (err) {
      setError(err?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filteredSchedules = schedules?.filter(schedule => {
    const matchesRoute = !selectedRoute || schedule?.route_id === selectedRoute;
    const route = routes?.find(r => r?.id === schedule?.route_id);
    const matchesSearch = !searchQuery || route?.route_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesRoute && matchesSearch;
  });

  const handleAddSchedule = () => {
    setSelectedSchedule(null);
    setIsFormModalOpen(true);
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setIsFormModalOpen(true);
  };

  const handleSaveSchedule = async (formData) => {
    try {
      if (selectedSchedule) {
        await scheduleService?.update(selectedSchedule?.id, {
          routeId: formData?.route_id,
          startTime: formData?.start_time,
          endTime: formData?.end_time,
          durationMinutes: parseInt(formData?.duration_minutes),
          frequencyMinutes: parseInt(formData?.frequency_minutes),
          daysMask: formData?.days_mask
        });
      } else {
        await scheduleService?.create({
          routeId: formData?.route_id,
          startTime: formData?.start_time,
          endTime: formData?.end_time,
          durationMinutes: parseInt(formData?.duration_minutes),
          frequencyMinutes: parseInt(formData?.frequency_minutes),
          daysMask: formData?.days_mask
        });
      }
      await loadData();
      setIsFormModalOpen(false);
      setSelectedSchedule(null);
    } catch (err) {
      setError(err?.message || 'Program kaydedilemedi');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm('Bu programı silmek istediğinizden emin misiniz?')) {
      try {
        await scheduleService?.delete(scheduleId);
        await loadData();
        setSelectedSchedules(prev => prev?.filter(id => id !== scheduleId));
      } catch (err) {
        setError(err?.message || 'Program silinemedi');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`${selectedSchedules?.length} programı silmek istediğinizden emin misiniz?`)) {
      try {
        await scheduleService?.bulkDelete(selectedSchedules);
        await loadData();
        setSelectedSchedules([]);
      } catch (err) {
        setError(err?.message || 'Toplu silme başarısız');
      }
    }
  };

  const handleSelectSchedule = (scheduleId) => {
    setSelectedSchedules(prev => 
      prev?.includes(scheduleId)
        ? prev?.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSchedules(filteredSchedules?.map(s => s?.id));
    } else {
      setSelectedSchedules([]);
    }
  };

  const handleApplyTemplate = (template, targetRoutes) => {
    const templateConfigs = {
      weekday_standard: {
        start_time: '06:00',
        end_time: '22:00',
        duration_minutes: 40,
        frequency_minutes: 15,
        days_mask: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      },
      weekend_standard: {
        start_time: '08:00',
        end_time: '20:00',
        duration_minutes: 40,
        frequency_minutes: 20,
        days_mask: ['saturday', 'sunday']
      },
      peak_hours: {
        start_time: '07:00',
        end_time: '09:00',
        duration_minutes: 30,
        frequency_minutes: 10,
        days_mask: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      },
      night_service: {
        start_time: '22:00',
        end_time: '06:00',
        duration_minutes: 60,
        frequency_minutes: 30,
        days_mask: ['friday', 'saturday', 'sunday']
      }
    };

    const config = templateConfigs?.[template];
    if (config) {
      const newSchedules = targetRoutes?.map(routeId => ({
        id: `schedule-${Date.now()}-${routeId}`,
        route_id: routeId,
        ...config
      }));
      setSchedules(prev => [...prev, ...newSchedules]);
    }
  };

  const handleDuplicate = (sourceRouteId, targetRoutes) => {
    const sourceSchedules = schedules?.filter(s => s?.route_id === sourceRouteId);
    const newSchedules = targetRoutes?.flatMap(targetRouteId =>
      sourceSchedules?.map(schedule => ({
        ...schedule,
        id: `schedule-${Date.now()}-${targetRouteId}-${schedule?.id}`,
        route_id: targetRouteId
      }))
    );
    setSchedules(prev => [...prev, ...newSchedules]);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(schedules, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `schedules-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (data) => {
    if (Array.isArray(data)) {
      setSchedules(prev => [...prev, ...data]);
    }
  };

  const handleSavePeakHours = (config) => {
    setPeakHours(config);
    setShowPeakConfig(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Program Yönetimi - Konya Dolmuş Saatim</title>
        <meta name="description" content="Dolmuş sefer programlarını yönetin, zamanlamaları düzenleyin ve toplu işlemler yapın" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userName="Admin"
          userRole="Sistem Yöneticisi"
        />

        <main className={`transition-smooth pt-16 lg:pt-0 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
          <div className="p-4 md:p-6 lg:p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading">
                  Program Yönetimi
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-2">
                  Sefer zamanlamalarını ve frekanslarını yapılandırın
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkPanel(!showBulkPanel)}
                  iconName="Layers"
                  iconPosition="left"
                  className="hidden md:flex"
                >
                  Toplu İşlemler
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPeakConfig(!showPeakConfig)}
                  iconName="TrendingUp"
                  iconPosition="left"
                  className="hidden md:flex"
                >
                  Yoğun Saatler
                </Button>
                <Button
                  variant="default"
                  onClick={handleAddSchedule}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Yeni Program
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="lg:col-span-2 space-y-4 md:space-y-6">
                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <Select
                        label="Rota Filtrele"
                        options={[
                          { value: '', label: 'Tüm Rotalar' },
                          ...routes?.map(route => ({
                            value: route?.id,
                            label: route?.route_name
                          }))
                        ]}
                        value={selectedRoute}
                        onChange={setSelectedRoute}
                        searchable
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        label="Ara"
                        type="search"
                        placeholder="Rota adı ile ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                      />
                    </div>
                  </div>

                  <ScheduleTable
                    schedules={filteredSchedules}
                    routes={routes}
                    onEdit={handleEditSchedule}
                    onDelete={handleDeleteSchedule}
                    onBulkDelete={handleBulkDelete}
                    selectedSchedules={selectedSchedules}
                    onSelectSchedule={handleSelectSchedule}
                    onSelectAll={handleSelectAll}
                  />
                </div>

                <SchedulePreview
                  schedules={filteredSchedules}
                  selectedRoute={selectedRoute}
                />
              </div>

              <div className="space-y-4 md:space-y-6">
                {showBulkPanel && (
                  <BulkOperationsPanel
                    routes={routes}
                    onApplyTemplate={handleApplyTemplate}
                    onDuplicate={handleDuplicate}
                    onExport={handleExport}
                    onImport={handleImport}
                  />
                )}

                {showPeakConfig && (
                  <PeakHoursConfig
                    peakHours={peakHours}
                    onSave={handleSavePeakHours}
                  />
                )}

                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="BarChart3" size={20} color="var(--color-primary)" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground font-heading">
                      İstatistikler
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Toplam Program</span>
                      <span className="text-lg font-bold text-foreground data-text">
                        {schedules?.length}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Aktif Rotalar</span>
                      <span className="text-lg font-bold text-foreground data-text">
                        {routes?.filter(r => r?.active)?.length}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Hafta İçi Programlar</span>
                      <span className="text-lg font-bold text-foreground data-text">
                        {schedules?.filter(s => s?.days_mask?.includes('monday'))?.length}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Hafta Sonu Programlar</span>
                      <span className="text-lg font-bold text-foreground data-text">
                        {schedules?.filter(s => s?.days_mask?.includes('saturday'))?.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-warning/10 rounded-lg">
                      <Icon name="Info" size={20} color="var(--color-warning)" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground font-heading">
                      Önemli Notlar
                    </h3>
                  </div>

                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-5 h-5 bg-primary/10 rounded-full flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} color="var(--color-primary)" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Başlangıç saati bitiş saatinden önce olmalıdır
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-5 h-5 bg-primary/10 rounded-full flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} color="var(--color-primary)" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Frekans değeri gerçekçi sefer aralıkları oluşturmalıdır
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-5 h-5 bg-primary/10 rounded-full flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} color="var(--color-primary)" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Çakışan programlar tahmin doğruluğunu etkileyebilir
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-5 h-5 bg-primary/10 rounded-full flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} color="var(--color-primary)" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Yoğun saat ayarları tüm rotalar için geçerlidir
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>

        <ScheduleFormModal
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedSchedule(null);
          }}
          onSave={handleSaveSchedule}
          schedule={selectedSchedule}
          routes={routes}
        />
      </div>
    </div>
  );
};

export default SchedulesManagement;